import React from "react";
import LegalLayout, { LEGAL, Section, ContactLine } from "./LegalLayout";
import "./Legal.css";

const SECTIONS = [
    { id: "who-we-are", title: "Who we are" },
    { id: "what-we-collect", title: "Information we collect" },
    { id: "how-we-use", title: "How we use it" },
    { id: "google-apple", title: "Google and Apple sign-in" },
    { id: "cookies", title: "Cookies, local storage and live chat" },
    { id: "sharing", title: "Who can see it" },
    { id: "storage", title: "Storage and retention" },
    { id: "security", title: "Security" },
    { id: "your-choices", title: "Your choices" },
    { id: "children", title: "Children" },
    { id: "changes", title: "Changes to this policy" },
    { id: "contact", title: "Contact us" },
];

const PrivacyPolicy = () => (
    <LegalLayout
        title="Privacy Policy"
        sections={SECTIONS}
        intro={(
            <>
                This policy explains what information {LEGAL.appName} collects, why, who can see it and the choices
                you have. {LEGAL.appName} is the tool {LEGAL.orgName} cell leaders and administrators use to record
                cell meetings, attendance and follow-up.
            </>
        )}
    >
        <Section id="who-we-are" title="Who we are">
            <p>
                {LEGAL.appName} is run by {LEGAL.orgName} (&ldquo;we&rdquo;, &ldquo;us&rdquo;) for its own ministry use.
                It is not a commercial service, and we do not sell, rent or trade personal information.
            </p>
        </Section>

        <Section id="what-we-collect" title="Information we collect">
            <p><strong>Your account.</strong> Your name, email address, username and role (cell leader, admin or super admin).
                If you sign in with a password, we store only a one-way hash of it, never the password itself.</p>
            <p><strong>Cell records entered by leaders and administrators.</strong></p>
            <ul>
                <li>Cells: name, type, meeting day and time, address and city, and map coordinates worked out from the address.</li>
                <li>Cell members and guests: name, phone number and birthday.</li>
                <li>Meeting reports: date, who attended, testimonies and evaluations. Evaluations may include prayer requests
                    and other personal or spiritual matters people chose to share.</li>
            </ul>
            <p><strong>Information from Google or Apple sign-in.</strong> If you choose to sign in with Google or Apple,
                we receive your account&rsquo;s unique ID and email address. See{" "}
                <a href="#google-apple">Google and Apple sign-in</a> for exactly what we receive, keep and delete.</p>
            <p><strong>Information kept in your browser.</strong> Your sign-in token and a few preferences are stored in
                your browser&rsquo;s local storage so the app works between visits (see{" "}
                <a href="#cookies">Cookies, local storage and live chat</a>).</p>
            <p><strong>Live chat messages.</strong> If you use the chat window, the messages you send and the details you
                choose to give (such as your name or email) are handled by our chat provider, Tidio.</p>
            <p><strong>Technical logs.</strong> Like most web services, our servers record basic request information
                (such as IP address, time and the page requested) to keep the service running and secure.</p>
        </Section>

        <Section id="how-we-use" title="How we use it">
            <ul>
                <li>To sign you in and show you the cells and pages your role allows.</li>
                <li>To record meetings and attendance, and to help leaders follow up with members and guests,
                    including remembering birthdays.</li>
                <li>To show cells on a map and summarise cell activity for church leadership.</li>
                <li>To keep the service secure, fix problems and prevent misuse.</li>
            </ul>
            <p>We do not use this information for advertising and we do not make automated decisions about people.</p>
        </Section>

        <Section id="google-apple" title="Google and Apple sign-in">
            <p>
                Signing in with Google or Apple is optional; you can also sign in with a password while it is still offered.
                We use Google Identity Services (&ldquo;Sign in with Google&rdquo;) and Sign in with Apple only to
                confirm who you are. We request only the basic sign-in permissions (OpenID, email and basic profile)
                and no access to any other Google or Apple data.
            </p>
            <p><strong>What we receive.</strong> When you sign in, Google or Apple sends us a signed sign-in token
                containing your account&rsquo;s unique ID, your email address and whether that email is verified.
                Google&rsquo;s token may also include your name and profile picture; we do not keep or use them.</p>
            <p><strong>What we keep.</strong> We store, in our own database, a link between your {LEGAL.appName}
                account and your Google or Apple account: the provider name, your account&rsquo;s unique ID, your
                email address and the date you last signed in with it. We never see or store your Google or Apple
                password, and we do not access your contacts, Gmail, Drive, calendar or any other data.</p>
            <p><strong>How we use it.</strong> Only to sign you in, to link the sign-in method to your existing
                account (we link automatically only when the email is verified and matches your {LEGAL.appName}
                account), and to keep your account secure.</p>
            <p><strong>Sharing.</strong> We do not sell, share or transfer data received from Google or Apple to anyone,
                and we do not use it for advertising, for profiling, or to develop or train artificial intelligence or
                machine-learning models. It is visible only to the system itself and, for account recovery, to church
                administrators, who can see which sign-in methods an account has linked.</p>
            <p><strong>Keeping and deleting it.</strong> The link is kept for as long as it stays connected to your
                account. You can delete it at any time in <strong>Settings → Sign-in Methods → Unlink</strong>, which
                removes it from our database straight away. An administrator can also remove it if you lose access to
                that account, and it is removed if you ask us to delete your account. You can also revoke
                {" "}{LEGAL.appName}&rsquo;s access from your Google Account at{" "}
                <a href="https://myaccount.google.com/connections" target="_blank" rel="noreferrer">myaccount.google.com/connections</a>.</p>
            <p>
                {LEGAL.appName}&rsquo;s use and transfer of information received from Google APIs adheres to the{" "}
                <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noreferrer">
                    Google API Services User Data Policy
                </a>, including the Limited Use requirements. If you use Apple&rsquo;s &ldquo;Hide My Email&rdquo;,
                we only see the relay address Apple provides.
            </p>
        </Section>

        <Section id="cookies" title="Cookies, local storage and live chat">
            <p>
                {LEGAL.appName} itself does not use advertising or analytics cookies. It keeps the following in your
                browser&rsquo;s local storage: your sign-in token and account details for the current session, which
                cell you last selected, whether the sidebar is collapsed, and whether you have seen the
                &ldquo;What&rsquo;s new&rdquo; tour. Signing out removes the sign-in token and account details.
            </p>
            <p>
                Our pages include a live chat window provided by Tidio. Tidio may set its own cookies and receive
                information such as your IP address, browser details, the pages you visit on this site and any
                messages you send, so it can provide the chat. See{" "}
                <a href="https://www.tidio.com/privacy-policy/" target="_blank" rel="noreferrer">Tidio&rsquo;s privacy policy</a>.
                Signing in with Google or Apple loads their sign-in scripts, which are covered by their own privacy policies.
            </p>
        </Section>

        <Section id="sharing" title="Who can see it">
            <ul>
                <li><strong>Cell leaders</strong> see the members, guests and reports for the cells they lead.</li>
                <li><strong>Administrators</strong> can see all cells, leaders and reports in order to oversee and care
                    for the cell ministry.</li>
                <li><strong>Service providers</strong> that run parts of the service for us, only as needed to provide it:
                    <ul>
                        <li>our hosting and database providers (such as DigitalOcean and Fly.io), which store and serve the data;</li>
                        <li>Google and Apple, when you choose to sign in with them;</li>
                        <li>Tidio, which provides the live chat window;</li>
                        <li>OpenStreetMap: cell addresses are sent to its geocoding service to find map coordinates, and
                            map images are loaded from its servers, which see your browser&rsquo;s IP address.</li>
                    </ul>
                </li>
            </ul>
            <p>We never sell personal information. We may disclose information if required by law or to protect someone&rsquo;s safety.</p>
        </Section>

        <Section id="storage" title="Storage and retention">
            <p>
                Information is stored on servers run by our hosting providers, which may be located outside Canada.
                When a member, guest or leader is removed in the app, the record is deactivated so that past
                attendance stays accurate. If you want information deleted permanently, contact us (see below)
                and we will remove it unless we need to keep it for a legitimate reason, such as a legal obligation.
                Deleted information may remain in backups for a limited time.
            </p>
        </Section>

        <Section id="security" title="Security">
            <p>
                We protect information with encrypted connections, hashed passwords, sign-in tokens that expire, and
                role-based access so people only see what their role needs. No system is perfectly secure; please
                keep your sign-in details to yourself and tell us straight away if you think your account has been misused.
            </p>
        </Section>

        <Section id="your-choices" title="Your choices">
            <ul>
                <li>You can ask to see, correct or delete information about you, including information a leader recorded
                    about you as a cell member or guest.</li>
                <li>You can update your profile and sign-in methods in <strong>Settings</strong>.</li>
                <li>You can ask us to deactivate your account at any time.</li>
            </ul>
            <p>To make a request, <ContactLine />.</p>
        </Section>

        <Section id="children" title="Children">
            <p>
                {LEGAL.appName} accounts are for adult leaders and administrators. If a leader records information about
                a child (for example in a family cell), they should do so only with a parent&rsquo;s or guardian&rsquo;s
                knowledge. A parent or guardian can ask us to correct or remove it.
            </p>
        </Section>

        <Section id="changes" title="Changes to this policy">
            <p>
                We may update this policy as {LEGAL.appName} changes. We will change the date at the top and, for
                significant changes, let users know in the app.
            </p>
        </Section>

        <Section id="contact" title="Contact us">
            <p>For questions about this policy or your information, <ContactLine />.</p>
        </Section>
    </LegalLayout>
);

export default PrivacyPolicy;
